if (!customElements.get('product-color-swatches')) {
  class ProductColorSwatches extends HTMLElement {
    constructor() {
      super();

      this.handleChange = this.handleChange.bind(this);
    }

    connectedCallback() { 
      this.addEventListener('change', this.handleChange);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.handleChange);
    }

    handleChange(event) {
      const input = event.target.closest('input[type="radio"][data-variant-image-600]');
      if (!input || !this.contains(input)) return;

      this.updateProductImage(input);
      this.updateProductForm(input);
    }

    updateProductImage(input) {
      const productCard = this.closest('.product-card');
      if (!productCard) return;

      const image = productCard?.querySelector('.product-card-media-image-primary');

      const image400 = input.getAttribute('data-variant-image-400');
      const image600 = input.getAttribute('data-variant-image-600');

      if (!image || !image600) return;

      image.src = image600;
      image.srcset = [
        image400 && `${image400} 400w`,
        `${image600} 600w`
      ].filter(Boolean).join(', ');

      image.alt = input.getAttribute('data-variant-image-alt') || '';
    }

    updateProductForm(input) {
      const productCard = this.closest('.product-card');
      const variantSelect = productCard?.querySelector('product-card-form select[name="id"]');

      if (!variantSelect) return;

      const optionPosition = input.dataset.optionPosition;
      const matchingOption = Array.from(variantSelect.options).find((option) => (
        option.getAttribute(`data-option-${optionPosition}`) === input.value && !option.disabled
      ));

      if (!matchingOption || matchingOption.selected) return;

      variantSelect.value = matchingOption.value;
      variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  customElements.define('product-color-swatches', ProductColorSwatches);
}

if (!customElements.get('product-card-form')) {
  class ProductCardForm extends HTMLElement {
    constructor() {
      super();

      this.handleChange = this.handleChange.bind(this);
    }

    connectedCallback() {
      this.addEventListener('change', this.handleChange);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.handleChange);
    }

    handleChange(event) {
      const select = event.target.closest('select[name="id"]');
      if (!select || !this.contains(select)) return;

      const selectedOption = select.options[select.selectedIndex];

      this.updateProductImage(selectedOption);
      this.updateProductSwatches(selectedOption);
      this.updateProductPrice(selectedOption);
      this.updateProductSaleBadge(selectedOption);
    }

    updateProductImage(selectedOption) {
      const productCard = this.closest('.product-card');
      const image = productCard?.querySelector('.product-card-media-image-primary');
      const image400 = selectedOption.getAttribute('data-variant-image-400');
      const image600 = selectedOption.getAttribute('data-variant-image-600');

      if (!image || !image600) return;

      image.src = image600;
      image.srcset = [
        image400 && `${image400} 400w`,
        `${image600} 600w`
      ].filter(Boolean).join(', ');
      image.alt = selectedOption.getAttribute('data-variant-image-alt') || '';
    }

    updateProductSwatches(selectedOption) {
      const productCard = this.closest('.product-card');
      const swatchInputs = productCard?.querySelectorAll('product-color-swatches input[type="radio"][data-option-position]');

      if (!swatchInputs?.length) return;

      swatchInputs.forEach((input) => {
        const optionValue = selectedOption.getAttribute(`data-option-${input.dataset.optionPosition}`);

        if (optionValue && input.value === optionValue && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    updateProductPrice(selectedOption) {
      const productCard = this.closest('.product-card');
      if (!productCard || !selectedOption?.value) return;

      productCard.dispatchEvent(new CustomEvent('product-card:variant-change', {
        bubbles: true,
        detail: {
          variantId: selectedOption.value,
        },
      }));
    }

    updateProductSaleBadge(selectedOption) {
      const productCard = this.closest('.product-card');
      const saleBadge = productCard?.querySelector('[data-product-card-sale-badge]');

      if (!saleBadge) return;

      const saleBadgeType = saleBadge.dataset.saleBadgeType === 'value' ? 'value' : 'percentage';
      const saleBadgeText = selectedOption.getAttribute(`data-sale-badge-${saleBadgeType}`) || '';

      saleBadge.textContent = saleBadgeText;
      saleBadge.hidden = saleBadgeText === '';
    }
  }

  customElements.define('product-card-form', ProductCardForm);
}

if (!customElements.get('product-price')) {
  class ProductPrice extends HTMLElement {
    constructor() {
      super();

      this.priceDataById = new Map();
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onProductCardVariantChange = this.onProductCardVariantChange.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.currentVariantId = this.dataset.currentVariantId;
      this.productCard = this.closest('.product-card');
      this.priceElement = this.querySelector('.product-price-values');
      this.comparePriceElement = this.querySelector('[data-compare-price]');
      this.priceElements = Array.from(this.querySelectorAll('[data-price]'));
      this.unitPriceElement = this.querySelector('[data-unit-price]');
      this.saleBadgeElement = this.querySelector('.product-price-sale-badge');
      this.priceDataById = this.getPriceDataById();

      if (this.productCard) {
        this.productCard.addEventListener('product-card:variant-change', this.onProductCardVariantChange);
      } else {
        document.addEventListener('product:variant-change', this.onVariantChange);
      }

      if (this.currentVariantId) this.updatePrice(this.currentVariantId);
    }

    disconnectedCallback() {
      if (this.productCard) {
        this.productCard.removeEventListener('product-card:variant-change', this.onProductCardVariantChange);
      } else {
        document.removeEventListener('product:variant-change', this.onVariantChange);
      }
    }

    getPriceDataById() {
      const valuesScript = this.querySelector('script[data-product-price-values]');
      if (!valuesScript) return new Map();

      try {
        const values = JSON.parse(valuesScript.textContent || '[]');
        if (!Array.isArray(values)) return new Map();
        return new Map(values.map((value) => [String(value.id), value]));
      } catch (error) {
        console.error('Error parsing product price values', error);
        return new Map();
      }
    }

    onVariantChange(event) {
      const detail = event.detail || {};
      const eventSectionId = detail.sectionId || null;
      const variantId = detail.variant?.id;

      if (this.sectionId && eventSectionId && this.sectionId !== String(eventSectionId)) return;
      if (!variantId) return;

      this.updatePrice(variantId);
    }

    onProductCardVariantChange(event) {
      const variantId = event.detail?.variantId;
      if (!variantId) return;

      this.updatePrice(variantId);
    }

    updatePrice(variantId) {
      const priceData = this.priceDataById.get(String(variantId));
      if (!priceData || !this.priceElement) return;

      this.priceElement.dataset.hasPriceCompare = priceData.hasCompare ? 'true' : 'false';
      this.priceElement.dataset.hasPriceUnit = priceData.hasUnit ? 'true' : 'false';
      this.priceElement.dataset.available = priceData.available ? 'true' : 'false';

      if (this.comparePriceElement) this.comparePriceElement.textContent = priceData.comparePrice || '';

      this.priceElements.forEach((element) => {
        element.textContent = priceData.price || '';
      });

      if (this.unitPriceElement) {
        this.unitPriceElement.textContent = priceData.hasUnit ? priceData.unitPrice || '' : '';
      }

      if (this.saleBadgeElement) {
        this.saleBadgeElement.textContent = priceData.saleBadge || '';
      }
    }
  }

  customElements.define('product-price', ProductPrice);
}

if (!customElements.get('product-options')) {
  class ProductOptions extends HTMLElement {
    constructor() {
      super();

      this.optionInputs = [];
      this.onOptionChange = this.onOptionChange.bind(this);
      this.updateActiveButtonIndicators = this.updateActiveButtonIndicators.bind(this);
    }

    connectedCallback() {
      this.optionInputs = Array.from(this.querySelectorAll('input[type="radio"], select'));
      this.optionInputs.forEach((input) => input.addEventListener('change', this.onOptionChange));
      this.handleOptionsAvailability();
      this.updateActiveButtonIndicators();
      window.addEventListener('resize', this.updateActiveButtonIndicators);
    }

    disconnectedCallback() {
      this.optionInputs.forEach((input) => input.removeEventListener('change', this.onOptionChange));
      window.removeEventListener('resize', this.updateActiveButtonIndicators);
    }

    getVariants() {
      const variantsScript = this.querySelector('script[data-product-variants]');
      if (!variantsScript) return [];

      try {
        const variants = JSON.parse(variantsScript.textContent || '[]');
        return Array.isArray(variants) ? variants : [];
      } catch (error) {
        console.error('Error parsing product variants', error);
        return [];
      }
    }

    getSelectedOptions() {
      const selectedOptions = [];
      const controls = Array.from(this.querySelectorAll('input[type="radio"]:checked, select'));

      controls.forEach((control) => {
        if (control.value !== '') selectedOptions.push(control.value);
      });

      return selectedOptions;
    }

    getSelectedOptionsByPosition() {
      const selectedOptions = {};
      const checkedRadios = Array.from(this.querySelectorAll('input[type="radio"][data-option-position]:checked'));
      const selects = Array.from(this.querySelectorAll('select[data-option-position]'));

      checkedRadios.forEach((input) => {
        const position = Number(input.dataset.optionPosition);
        if (Number.isFinite(position) && position > 0) selectedOptions[position] = input.value;
      });

      selects.forEach((select) => {
        const position = Number(select.dataset.optionPosition);
        if (Number.isFinite(position) && position > 0 && select.value !== '') selectedOptions[position] = select.value;
      });

      return selectedOptions;
    }

    findSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      if (!selectedOptions.length) return null;

      return this.getVariants().find((variant) => {
        const variantOptions = [variant.option1, variant.option2, variant.option3];
        return selectedOptions.every((optionValue, optionIndex) => variantOptions[optionIndex] === optionValue);
      }) || null;
    }

    isOptionValueAvailable(position, value, selectedByPosition) {
      return this.getVariants().some((variant) => {
        if (!variant.available) return false;

        const variantOptions = [variant.option1, variant.option2, variant.option3];

        for (let index = 1; index <= 3; index += 1) {
          const selectedValue = index === position ? value : selectedByPosition[index];
          if (selectedValue && variantOptions[index - 1] !== selectedValue) return false;
        }

        return true;
      });
    }

    handleOptionsAvailability() {
      const selectedByPosition = this.getSelectedOptionsByPosition();

      this.querySelectorAll('input[type="radio"][data-option-position]').forEach((input) => {
        const position = Number(input.dataset.optionPosition);
        if (!Number.isFinite(position) || position < 1) return;

        const isAvailable = this.isOptionValueAvailable(position, input.value, selectedByPosition);
        input.classList.toggle('disabled', !isAvailable);

        if (isAvailable) {
          input.removeAttribute('aria-disabled');
        } else {
          input.setAttribute('aria-disabled', 'true');
        }
      });

      this.querySelectorAll('select[data-option-position]').forEach((select) => {
        const position = Number(select.dataset.optionPosition);
        if (!Number.isFinite(position) || position < 1) return;

        Array.from(select.options).forEach((option) => {
          const isAvailable = this.isOptionValueAvailable(position, option.value, selectedByPosition);
          option.classList.toggle('disabled', !isAvailable);

          if (isAvailable) {
            option.removeAttribute('aria-disabled');
          } else {
            option.setAttribute('aria-disabled', 'true');
          }
        });
      });
    }

    updateSelectedOptionLabels() {
      this.querySelectorAll('[data-selected-option-value]').forEach((label) => {
        const fieldset = label.closest('fieldset');
        const checkedRadio = fieldset?.querySelector('input[type="radio"]:checked');
        if (checkedRadio) label.textContent = checkedRadio.value;
      });
    }

    updateActiveButtonIndicators() {
      this.querySelectorAll('.product-options-buttons').forEach((group) => {
        const checkedInput = group.querySelector('input[type="radio"]:checked');
        const label = checkedInput?.nextElementSibling;

        if (!(label instanceof HTMLElement)) {
          group.style.setProperty('--product-options-active-opacity', '0');
          return;
        }

        group.style.setProperty('--product-options-active-left', `${label.offsetLeft}px`);
        group.style.setProperty('--product-options-active-top', `${label.offsetTop}px`);
        group.style.setProperty('--product-options-active-width', `${label.offsetWidth}px`);
        group.style.setProperty('--product-options-active-height', `${label.offsetHeight}px`);
        group.style.setProperty('--product-options-active-opacity', '1');
      });
    }

    updateVariantUrl(variant) {
      if (this.closest('.featured-product')) return;
      if (this.closest('.product-quick-view')) return;

      const url = new URL(window.location.href);

      if (variant?.id) {
        url.searchParams.set('variant', String(variant.id));
      } else {
        url.searchParams.delete('variant');
      }

      window.history.replaceState({}, '', url.toString());
    }

    onOptionChange() {
      this.updateSelectedOptionLabels();
      this.handleOptionsAvailability();
      this.updateActiveButtonIndicators();

      const selectedVariant = this.findSelectedVariant();
      this.updateVariantUrl(selectedVariant);

      document.dispatchEvent(new CustomEvent('product:variant-change', {
        detail: {
          sectionId: this.dataset.sectionId,
          variant: selectedVariant || null,
        },
      }));
    }
  }

  customElements.define('product-options', ProductOptions);
}

if (!customElements.get('product-buy-buttons')) {
  class ProductBuyButtons extends HTMLElement {
    constructor() {
      super();

      this.currentVariant = null;
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onQuantityChange = this.onQuantityChange.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.quantityInput = this.querySelector('[data-quantity-input]');
      this.decreaseButton = this.querySelector('[data-quantity-decrease]');
      this.increaseButton = this.querySelector('[data-quantity-increase]');
      this.addToCartButton = this.querySelector('[data-add-to-cart-button]');
      this.addToCartText = this.querySelector('[data-add-to-cart-text]');
      this.addToCartIcon = this.querySelector('[data-add-to-cart-icon]');
      this.buyNowButton = this.querySelector('[data-buy-now-button]');
      this.quickViewDynamicCheckout = this.querySelector('[data-quick-view-dynamic-checkout]');
      this.quickViewDynamicQuantityInput = this.querySelector('[data-native-dynamic-quantity]');

      const variantId = this.querySelector('[data-product-variant-input]')?.value;
      this.currentVariant = this.findVariantById(variantId);

      document.addEventListener('product:variant-change', this.onVariantChange);
      this.addEventListener('click', this.onClick);
      this.quantityInput?.addEventListener('change', this.onQuantityChange);
      this.updateButtonState();
      this.updateQuantityButtons();
      this.syncQuickViewDynamicCheckout();
    }

    disconnectedCallback() {
      document.removeEventListener('product:variant-change', this.onVariantChange);
      this.removeEventListener('click', this.onClick);
      this.quantityInput?.removeEventListener('change', this.onQuantityChange);
    }

    getVariants() {
      const variantsScript = this.querySelector('script[data-product-variants]');
      if (!variantsScript) return [];

      try {
        const variants = JSON.parse(variantsScript.textContent || '[]');
        return Array.isArray(variants) ? variants : [];
      } catch (error) {
        console.error('Error parsing product button variants', error);
        return [];
      }
    }

    findVariantById(variantId) {
      if (!variantId) return null;
      return this.getVariants().find((variant) => String(variant.id) === String(variantId)) || null;
    }

    getQuantity() {
      if (!this.quantityInput) return 1;
      const min = Number(this.quantityInput.min) || 1;
      const max = this.quantityInput.max ? Number(this.quantityInput.max) : Infinity;
      const value = Number(this.quantityInput.value) || min;
      return Math.min(Math.max(value, min), max);
    }

    getVariantMax(variant) {
      if (!variant || variant.inventory_management === null || variant.inventory_policy !== 'deny') return null;
      const quantity = Number(variant.inventory_quantity) || 0;
      return quantity > 0 ? quantity : null;
    }

    setVariantInputs(variantId) {
      this.querySelectorAll('[data-product-variant-input]').forEach((input) => {
        input.value = variantId || '';
      });
    }

    updateQuantityMax() {
      if (!this.quantityInput) return;

      const max = this.getVariantMax(this.currentVariant);
      if (max === null) {
        this.quantityInput.removeAttribute('max');
      } else {
        this.quantityInput.max = String(max);
      }

      this.quantityInput.value = String(this.getQuantity());
    }

    updateQuantityButtons() {
      if (!this.quantityInput) return;

      const quantity = this.getQuantity();
      const min = Number(this.quantityInput.min) || 1;
      const max = this.quantityInput.max ? Number(this.quantityInput.max) : Infinity;

      this.decreaseButton?.toggleAttribute('disabled', quantity <= min);
      this.increaseButton?.toggleAttribute('disabled', quantity >= max);
    }

    syncQuickViewDynamicCheckout() {
      if (!this.quickViewDynamicCheckout) return;

      if (this.quickViewDynamicQuantityInput) {
        this.quickViewDynamicQuantityInput.value = String(this.getQuantity());
      }

      this.quickViewDynamicCheckout.hidden = !this.currentVariant?.available;
    }

    updateButtonState() {
      const available = Boolean(this.currentVariant?.available);

      this.addToCartButton?.toggleAttribute('disabled', !available);
      this.buyNowButton?.toggleAttribute('disabled', !available);

      if (this.addToCartText) {
        this.addToCartText.textContent = available ? this.dataset.addToCartText : this.dataset.soldOutText;
      }

      this.addToCartIcon?.toggleAttribute('hidden', !available);
    }

    onVariantChange(event) {
      const eventSectionId = event.detail?.sectionId || null;
      if (this.sectionId && eventSectionId && this.sectionId !== String(eventSectionId)) return;

      const variantId = event.detail?.variant?.id;
      this.currentVariant = this.findVariantById(variantId) || event.detail?.variant || null;
      this.dataset.selectedMediaId = this.currentVariant?.featured_media_id || '';
      this.setVariantInputs(variantId);
      this.updateQuantityMax();
      this.updateQuantityButtons();
      this.updateButtonState();
      this.syncQuickViewDynamicCheckout();
    }

    onQuantityChange() {
      if (!this.quantityInput) return;
      this.quantityInput.value = String(this.getQuantity());
      this.updateQuantityButtons();
      this.syncQuickViewDynamicCheckout();
    }

    getBuyNowItems() {
      const variantId = this.currentVariant?.id;
      if (!variantId) return [];

      const items = [{ id: variantId, quantity: this.getQuantity() }];
      const productScope = this.closest('.product-main-content, .product-main');
      const selectedOption = productScope?.querySelector('product-bundle-selector [data-bundle-option-input]:checked');

      if (!selectedOption) {
        this.getSelectedUpsellItems().forEach((item) => {
          items.push(item);
        });

        return items;
      }

      const selectedIndex = Number(selectedOption.dataset.optionIndex || 0);
      const giftVariantIds = Array.from(productScope.querySelectorAll('product-bundle-selector [data-bundle-gift-option][data-variant-id]'))
        .filter((option) => Number(option.dataset.optionIndex || 0) <= selectedIndex)
        .map((option) => option.dataset.variantId)
        .filter(Boolean);

      giftVariantIds.forEach((giftVariantId) => {
        items.push({ id: giftVariantId, quantity: 1 });
      });

      this.getSelectedUpsellItems().forEach((item) => {
        items.push(item);
      });

      return items;
    }

    getSelectedUpsellItems() {
      const productScope = this.closest('.product-main-content, .product-main');
      return Array.from(productScope?.querySelectorAll('[data-product-upsell-input]:checked') || [])
        .map((input) => ({
          id: input.value,
          quantity: Number(input.dataset.quantity || 1),
        }))
        .filter((item) => item.id);
    }

    onClick(event) {
      const decreaseButton = event.target.closest('[data-quantity-decrease]');
      const increaseButton = event.target.closest('[data-quantity-increase]');
      const buyNowButton = event.target.closest('[data-buy-now-button]');

      if (decreaseButton && this.contains(decreaseButton)) {
        this.quantityInput.value = String(this.getQuantity() - 1);
        this.onQuantityChange();
        return;
      }

      if (increaseButton && this.contains(increaseButton)) {
        this.quantityInput.value = String(this.getQuantity() + 1);
        this.onQuantityChange();
        return;
      }

      if (buyNowButton && this.contains(buyNowButton)) {
        if (!this.currentVariant?.available) return;
        const items = this.getBuyNowItems();
        if (!items.length) return;
        const cartItems = items.map((item) => `${encodeURIComponent(item.id)}:${encodeURIComponent(item.quantity)}`).join(',');
        window.location.href = `/cart/${cartItems}`;
      }
    }
  }

  customElements.define('product-buy-buttons', ProductBuyButtons);
}

if (!customElements.get('product-personalization-field')) {
  class ProductPersonalizationField extends HTMLElement {
    constructor() {
      super();

      this.onChange = this.onChange.bind(this);
    }

    connectedCallback() {
      this.addEventListener('change', this.onChange);
      this.syncCheckboxValue();
      this.syncFormAttribute();

      window.requestAnimationFrame(() => {
        this.syncFormAttribute();
      });

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.syncFormAttribute(), { once: true });
      }
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
    }

    getProductScope() {
      return this.closest('.product-main-content, .product-main');
    }

    getTargetForm() {
      return this.getProductScope()?.querySelector('product-buy-buttons form.product-buttons-form') || null;
    }

    syncFormAttribute() {
      const targetForm = this.getTargetForm();
      const targetFormId = targetForm?.getAttribute('id');
      if (!targetFormId) return;

      this.querySelectorAll('[data-product-personalization-input]').forEach((input) => {
        input.setAttribute('form', targetFormId);
      });
    }

    syncCheckboxValue() {
      const hiddenInput = this.querySelector('[data-product-personalization-checkbox-value]');
      if (!hiddenInput) return;

      const checkedValues = Array.from(this.querySelectorAll('[data-product-personalization-checkbox]:checked'))
        .map((checkbox) => checkbox.value)
        .filter(Boolean);

      hiddenInput.value = checkedValues.join(', ');
    }

    onChange(event) {
      if (event.target.matches('[data-product-personalization-checkbox]')) {
        this.syncCheckboxValue();
      }

      this.syncFormAttribute();
    }
  }

  customElements.define('product-personalization-field', ProductPersonalizationField);
}

if (!customElements.get('product-upsell-checkbox')) {
  class ProductUpsellCheckbox extends HTMLElement {
    constructor() {
      super();

      this.onVariantChange = this.onVariantChange.bind(this);
    }

    connectedCallback() {
      this.checkbox = this.querySelector('[data-product-upsell-input]');
      this.variantSelect = this.querySelector('[data-product-upsell-variant-select]');
      this.label = this.querySelector('[data-product-upsell-label]');
      this.image = this.querySelector('[data-product-upsell-image] img');

      this.variantSelect?.addEventListener('change', this.onVariantChange);
      this.onVariantChange();
    }

    disconnectedCallback() {
      this.variantSelect?.removeEventListener('change', this.onVariantChange);
    }

    onVariantChange() {
      if (!this.checkbox || !this.variantSelect) return;

      const selectedOption = this.variantSelect.selectedOptions[0];
      if (!selectedOption) return;

      this.checkbox.value = selectedOption.value;

      if (this.label && this.dataset.labelTemplate) {
        this.label.innerHTML = this.dataset.labelTemplate.replace('[price]', selectedOption.dataset.priceLabel || '');
      }

      const variantImage120 = selectedOption.getAttribute('data-variant-image-120');
      const variantImage240 = selectedOption.getAttribute('data-variant-image-240');

      if (this.image && variantImage240) {
        this.image.src = variantImage240;
        this.image.srcset = [
          variantImage120 && `${variantImage120} 120w`,
          `${variantImage240} 240w`
        ].filter(Boolean).join(', ');
        this.image.alt = selectedOption.getAttribute('data-variant-image-alt') || '';
      }
    }
  }

  customElements.define('product-upsell-checkbox', ProductUpsellCheckbox);
}

if (!customElements.get('product-qty-breaks')) {
  class ProductQtyBreaks extends HTMLElement {
    constructor() {
      super();

      this.onChange = this.onChange.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.variants = this.getVariants();
      this.addEventListener('change', this.onChange);
      document.addEventListener('product:variant-change', this.onVariantChange);
      this.updateAllBreaks();
      this.updateSelectTabIndexes();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      document.removeEventListener('product:variant-change', this.onVariantChange);
    }

    getVariants() {
      const script = this.querySelector('script[data-product-qty-break-variants]');
      if (!script) return [];

      try {
        const variants = JSON.parse(script.textContent || '[]');
        return Array.isArray(variants) ? variants : [];
      } catch (error) {
        console.error('Error parsing quantity break variants', error);
        return [];
      }
    }

    findVariantByOptions(options) {
      return this.variants.find((variant) => {
        return Array.isArray(variant.options) && variant.options.every((option, index) => option === options[index]);
      });
    }

    getSelectedVariant(element) {
      const options = Array.from(element.querySelectorAll('[data-qty-break-option]')).map((select) => select.value);
      return this.findVariantByOptions(options);
    }

    updateBreak(group) {
      const selectedVariants = Array.from(group.querySelectorAll('[data-qty-break-variant]'))
        .map((element) => {
          const variant = this.getSelectedVariant(element);
          const soldOut = element.querySelector('.product-qty-break-variant-sold-out');
          soldOut?.toggleAttribute('hidden', Boolean(variant?.available));
          return variant;
        })
        .filter(Boolean);
      const input = group.querySelector('[data-qty-break-input]');

      if (!input || !selectedVariants.length) return;

      input.value = selectedVariants.map((variant) => variant.id).join(',');
      input.disabled = selectedVariants.some((variant) => !variant.available);
      this.updateTotals(group, selectedVariants);
    }

    updateTotals(group, variants) {
      const totalPrice = variants.reduce((total, variant) => total + Number(variant.price || 0), 0);
      const discountType = group.dataset.discountType;
      const discountPercent = Number(group.dataset.discountPercent || 0);
      const discountValue = Number(group.dataset.discountValue || 0);
      const discountedTotal = discountType === 'percentage'
        ? totalPrice * (100 - discountPercent) / 100
        : Math.max(totalPrice - discountValue, 0);
      const formattedTotal = Math.round(discountedTotal);
      const totalElement = group.querySelector('[data-qty-break-total]');
      const compareElement = group.querySelector('[data-qty-break-compare-total]');
      const compareWrapper = group.querySelector('.product-qty-break-total s');

      if (totalElement) {
        totalElement.textContent = window.Shopify.formatMoney(formattedTotal);
      }

      if (compareElement) {
        compareElement.textContent = window.Shopify.formatMoney(totalPrice);
      }

      compareWrapper?.toggleAttribute('hidden', totalPrice <= discountedTotal);
    }

    updateAllBreaks() {
      this.querySelectorAll('[data-qty-break]').forEach((group) => this.updateBreak(group));
    }

    updateSelectTabIndexes() {
      this.querySelectorAll('[data-qty-break]').forEach((group) => {
        const checked = group.querySelector('[data-qty-break-input]')?.checked;

        group.querySelectorAll('[data-qty-break-option]').forEach((select) => {
          if (checked) {
            select.removeAttribute('tabindex');
          } else {
            select.setAttribute('tabindex', '-1');
          }
        });
      });
    }

    setOptionsToVariant(variant) {
      if (!variant?.options) return;

      this.querySelectorAll('[data-qty-break-variant]').forEach((element) => {
        element.querySelectorAll('[data-qty-break-option]').forEach((select, index) => {
          select.value = variant.options[index] || select.value;
        });
      });
    }

    onChange() {
      this.updateAllBreaks();
      this.updateSelectTabIndexes();
    }

    onVariantChange(event) {
      const eventSectionId = event.detail?.sectionId || null;
      if (this.sectionId && eventSectionId && this.sectionId !== String(eventSectionId)) return;

      this.setOptionsToVariant(event.detail?.variant);
      this.updateAllBreaks();
      this.updateSelectTabIndexes();
    }
  }

  customElements.define('product-qty-breaks', ProductQtyBreaks);
}

if (!customElements.get('product-bundle-selector')) {
  class ProductBundleSelector extends HTMLElement {
    constructor() {
      super();

      this.unlockTimers = new Map();
      this.onChange = this.onChange.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.variants = this.getVariants();
      this.addEventListener('change', this.onChange);
      document.addEventListener('product:variant-change', this.onVariantChange);
      this.updateGift();
      this.updateQuantityInput();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      document.removeEventListener('product:variant-change', this.onVariantChange);
      this.unlockTimers.forEach((timer) => window.clearTimeout(timer));
      this.unlockTimers.clear();
    }

    getVariants() {
      const script = this.querySelector('script[data-product-bundle-variants]');
      if (!script) return [];

      try {
        const variants = JSON.parse(script.textContent || '[]');
        return Array.isArray(variants) ? variants : [];
      } catch (error) {
        console.error('Error parsing bundle selector variants', error);
        return [];
      }
    }

    findVariantById(variantId) {
      return this.variants.find((variant) => String(variant.id) === String(variantId)) || null;
    }

    updatePrices(variant) {
      if (!variant) return;

      this.querySelectorAll('[data-bundle-option-price]').forEach((priceElement) => {
        const label = priceElement.closest('label');
        const quantity = Number(priceElement.dataset.quantity || 1);
        const compareTotal = Number(variant.price || 0) * quantity;
        const discountType = label?.dataset.discountType;
        const discountPercent = Number(label?.dataset.discountPercent || 0);
        const discountValue = Number(label?.dataset.discountValue || 0);
        const discountedTotal = discountType === 'percentage'
          ? compareTotal * (100 - discountPercent) / 100
          : Math.max(compareTotal - discountValue, 0);
        const total = Math.round(discountedTotal);
        const compareWrapper = label?.querySelector('.product-bundle-option-price-wrapper s');
        const compareElement = label?.querySelector('[data-bundle-option-compare-price]');

        priceElement.textContent = window.Shopify.formatMoney(total);

        if (compareElement) {
          compareElement.textContent = window.Shopify.formatMoney(compareTotal);
        }

        compareWrapper?.toggleAttribute('hidden', compareTotal <= total);
      });
    }

    updateGift(animate = false) {
      const selectedOption = this.querySelector('[data-bundle-option-input]:checked');
      const gift = this.querySelector('[data-bundle-gift]');

      if (!gift) return;

      const selectedIndex = Number(selectedOption?.dataset.optionIndex || 0);
      const giftOptions = Array.from(this.querySelectorAll('[data-bundle-gift-option]'));
      const giftCount = gift.querySelector('[data-bundle-gift-count]');
      let unlockedGiftCount = 0;

      giftOptions.forEach((option) => {
        const optionIndex = Number(option.dataset.optionIndex || 0);
        const enabled = selectedIndex > 0 && optionIndex <= selectedIndex;
        const wasEnabled = option.classList.contains('is-enabled');
        const unlockTimer = this.unlockTimers.get(option);

        if (unlockTimer) window.clearTimeout(unlockTimer);
        this.unlockTimers.delete(option);
        option.classList.remove('is-unlocking');
        option.setAttribute('aria-hidden', enabled ? 'false' : 'true');
        option.classList.toggle('is-enabled', enabled);
        if (enabled) unlockedGiftCount += 1;

        if (animate && enabled && !wasEnabled) {
          option.classList.add('is-unlocking');
          this.unlockTimers.set(option, window.setTimeout(() => {
            option.classList.remove('is-unlocking');
            this.unlockTimers.delete(option);
          }, 550));
        }
      });

      if (giftCount) {
        giftCount.textContent = unlockedGiftCount;
      }
    }

    updateQuantityInput() {
      const selectedOption = this.querySelector('[data-bundle-option-input]:checked');
      const productScope = this.closest('.product-main-content, .product-main');
      const quantityInput = productScope?.querySelector('product-buy-buttons [data-quantity-input]');

      if (!selectedOption?.value || !quantityInput) return;

      quantityInput.value = selectedOption.value;
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    onChange() {
      this.updateGift(true);
      this.updateQuantityInput();
    }

    onVariantChange(event) {
      const eventSectionId = event.detail?.sectionId || null;
      if (this.sectionId && eventSectionId && this.sectionId !== String(eventSectionId)) return;

      const variant = this.findVariantById(event.detail?.variant?.id);
      this.updatePrices(variant);
    }
  }

  customElements.define('product-bundle-selector', ProductBundleSelector);
}

if (!customElements.get('product-inventory-bar')) {
  class ProductInventoryBar extends HTMLElement {
    constructor() {
      super();

      this.valuesById = new Map();
      this.onVariantChange = this.onVariantChange.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.textElement = this.querySelector('[data-inventory-bar-text]');
      this.progressElement = this.querySelector('[data-inventory-bar-progress]');
      this.progressFillElement = this.querySelector('[data-inventory-bar-progress-fill]');
      this.valuesById = this.getValuesById();

      document.addEventListener('product:variant-change', this.onVariantChange);
      this.animateProgress();
    }

    disconnectedCallback() {
      document.removeEventListener('product:variant-change', this.onVariantChange);
    }

    getValuesById() {
      const valuesScript = this.querySelector('script[data-product-inventory-bar-values]');
      if (!valuesScript) return new Map();

      try {
        const values = JSON.parse(valuesScript.textContent || '[]');
        if (!Array.isArray(values)) return new Map();
        return new Map(values.map((value) => [String(value.id), value]));
      } catch (error) {
        console.error('Error parsing product inventory bar values', error);
        return new Map();
      }
    }

    onVariantChange(event) {
      const detail = event.detail || {};
      const eventSectionId = detail.sectionId || null;
      const variantId = detail.variant?.id;

      if (this.sectionId && eventSectionId && this.sectionId !== String(eventSectionId)) return;
      if (!variantId) return;

      this.updateInventoryBar(variantId);
    }

    updateInventoryBar(variantId) {
      const value = this.valuesById.get(String(variantId));
      if (!value) return;

      const percentage = Math.min(Math.max(Number(value.percentage) || 0, 0), 100);

      this.style.setProperty('--product-inventory-bar-color-rgb', value.colorRgb);

      if (this.textElement) {
        this.textElement.innerHTML = value.text || '';
      }

      if (this.progressElement) {
        this.progressElement.setAttribute('aria-valuenow', String(percentage));
      }

      if (this.progressFillElement) {
        this.progressFillElement.dataset.width = `${percentage}%`;

        if (this.dataset.animateProgressWidth === 'true') {
          this.progressFillElement.style.width = '0%';
          this.animateProgress();
        } else {
          this.progressFillElement.style.width = `${percentage}%`;
        }
      }
    }

    animateProgress() {
      if (!this.progressFillElement) return;

      window.requestAnimationFrame(() => {
        this.progressFillElement.style.width = this.progressFillElement.dataset.width || '0%';
      });
    }
  }

  customElements.define('product-inventory-bar', ProductInventoryBar);
}

if (!customElements.get('product-gallery')) {
  class ProductGallery extends HTMLElement {
    constructor() {
      super();

      this.items = [];
      this.thumbs = [];
      this.activeIndex = 0;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchDeltaX = 0;
      this.touchDeltaY = 0;
      this.isTouchTracking = false;
      this.onPrevClick = this.onPrevClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);
      this.onThumbClick = this.onThumbClick.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.items = Array.from(this.querySelectorAll('[data-gallery-item]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.thumbsList = this.querySelector('.product-gallery-thumbnails');
      this.counterCurrent = this.querySelector('[data-gallery-counter-current]');
      this.counterTotal = this.querySelector('[data-gallery-counter-total]');
      this.main = this.querySelector('.product-gallery-main');
      this.prevButtons = Array.from(this.querySelectorAll('[data-gallery-prev]'));
      this.nextButtons = Array.from(this.querySelectorAll('[data-gallery-next]'));

      if (!this.items.length) return;

      this.activeIndex = this.items.findIndex((item) => item.classList.contains('active'));
      if (this.activeIndex < 0) this.activeIndex = 0;

      this.prevButtons.forEach((button) => button.addEventListener('click', this.onPrevClick));
      this.nextButtons.forEach((button) => button.addEventListener('click', this.onNextClick));
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', this.onThumbClick));

      this.main?.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.main?.addEventListener('touchmove', this.onTouchMove, { passive: true });
      this.main?.addEventListener('touchend', this.onTouchEnd);
      this.main?.addEventListener('touchcancel', this.onTouchEnd);
      document.addEventListener('product:variant-change', this.onVariantChange);

      this.showItem(this.activeIndex);
    }

    disconnectedCallback() {
      this.prevButtons.forEach((button) => button.removeEventListener('click', this.onPrevClick));
      this.nextButtons.forEach((button) => button.removeEventListener('click', this.onNextClick));
      this.thumbs.forEach((thumb) => thumb.removeEventListener('click', this.onThumbClick));
      this.main?.removeEventListener('touchstart', this.onTouchStart);
      this.main?.removeEventListener('touchmove', this.onTouchMove);
      this.main?.removeEventListener('touchend', this.onTouchEnd);
      this.main?.removeEventListener('touchcancel', this.onTouchEnd);
      document.removeEventListener('product:variant-change', this.onVariantChange);
    }

    onVariantChange(event) {
      const variant = event.detail?.variant || null;
      const eventSectionId = event.detail?.sectionId || null;

      if (this.sectionId && eventSectionId && this.sectionId !== String(eventSectionId)) return;
      if (!variant?.featured_media_id) return;

      this.updateVariantMedia(variant.featured_media_id);

      const targetIndex = this.getVisibleItems().findIndex((item) => item.dataset.mediaId === String(variant.featured_media_id));
      if (targetIndex >= 0) this.showItem(targetIndex);
    }

    getVisibleItems() {
      return this.items.filter((item) => !item.hidden);
    }

    getVisibleThumbs() {
      return this.thumbs.filter((thumb) => !thumb.closest('.product-gallery-thumbnail-item')?.hidden);
    }

    updateVariantMedia(featuredMediaId) {
      if (this.dataset.hideVariants !== 'true' || !featuredMediaId) return;

      const mediaId = String(featuredMediaId);

      this.items.forEach((item) => {
        if (!item.hasAttribute('data-variant-media')) return;
        item.hidden = item.dataset.mediaId !== mediaId;
      });

      this.thumbs.forEach((thumb) => {
        const item = thumb.closest('.product-gallery-thumbnail-item');
        if (!item?.hasAttribute('data-variant-media')) return;
        item.hidden = item.dataset.mediaId !== mediaId;
      });
    }

    onPrevClick() {
      this.showItem(this.activeIndex - 1);
    }

    onNextClick() {
      this.showItem(this.activeIndex + 1);
    }

    onThumbClick(event) {
      const index = this.getVisibleThumbs().indexOf(event.currentTarget);
      if (index >= 0) this.showItem(index);
    }

    onTouchStart(event) {
      if (event.touches.length !== 1 || this.getVisibleItems().length < 2) {
        this.isTouchTracking = false;
        return;
      }

      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      this.touchDeltaX = 0;
      this.touchDeltaY = 0;
      this.isTouchTracking = true;
    }

    onTouchMove(event) {
      if (!this.isTouchTracking || event.touches.length !== 1) return;

      this.touchDeltaX = event.touches[0].clientX - this.touchStartX;
      this.touchDeltaY = event.touches[0].clientY - this.touchStartY;
    }

    onTouchEnd() {
      if (!this.isTouchTracking) return;

      this.isTouchTracking = false;
      const horizontalDistance = Math.abs(this.touchDeltaX);
      const verticalDistance = Math.abs(this.touchDeltaY);

      if (horizontalDistance < 50 || horizontalDistance <= verticalDistance) return;
      this.showItem(this.activeIndex + (this.touchDeltaX < 0 ? 1 : -1));
    }

    pauseMediaInItem(item) {
      item.querySelectorAll('video').forEach((video) => video.pause());
    }

    playMediaInItem(item) {
      item.querySelectorAll('video[autoplay], video[data-autoplay]').forEach((video) => {
        video.play().catch(() => {});
      });
    }

    updateThumbs() {
      const visibleThumbs = this.getVisibleThumbs();

      this.thumbs.forEach((thumb) => {
        thumb.classList.remove('active');
        thumb.removeAttribute('aria-current');
      });

      visibleThumbs.forEach((thumb, index) => {
        const active = index === this.activeIndex;
        thumb.classList.toggle('active', active);
        thumb.toggleAttribute('aria-current', active);
      });

      const activeThumb = visibleThumbs[this.activeIndex];
      if (!this.thumbsList || !activeThumb) return;

      const anchor = activeThumb.closest('.product-gallery-thumbnail-item') || activeThumb;
      const listRect = this.thumbsList.getBoundingClientRect();
      const anchorRect = anchor.getBoundingClientRect();
      const maxScrollLeft = this.thumbsList.scrollWidth - this.thumbsList.clientWidth;
      const relativeLeft = this.thumbsList.scrollLeft + (anchorRect.left - listRect.left);

      this.thumbsList.scrollTo({
        left: Math.min(Math.max(relativeLeft, 0), Math.max(maxScrollLeft, 0)),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    }

    updateCounter() {
      const visibleItems = this.getVisibleItems();
      if (this.counterCurrent) this.counterCurrent.textContent = String(this.activeIndex + 1);
      if (this.counterTotal) this.counterTotal.textContent = String(visibleItems.length);
    }

    showItem(index) {
      const visibleItems = this.getVisibleItems();
      if (!visibleItems.length) return;

      const nextIndex = ((index % visibleItems.length) + visibleItems.length) % visibleItems.length;
      this.items.forEach((item) => this.pauseMediaInItem(item));

      this.items.forEach((item) => {
        const active = item === visibleItems[nextIndex];
        item.classList.toggle('active', active);
        item.setAttribute('aria-hidden', String(!active));
        item.toggleAttribute('inert', !active);
      });

      this.activeIndex = nextIndex;
      this.prevButtons.forEach((button) => button.toggleAttribute('disabled', visibleItems.length < 2));
      this.nextButtons.forEach((button) => button.toggleAttribute('disabled', visibleItems.length < 2));
      this.updateThumbs();
      this.updateCounter();
      this.playMediaInItem(visibleItems[nextIndex]);
    }
  }

  customElements.define('product-gallery', ProductGallery);
}

if (!customElements.get('product-gallery-desktop')) {
  class ProductGalleryDesktop extends HTMLElement {
    constructor() {
      super();

      this.onVariantChange = this.onVariantChange.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.items = Array.from(this.querySelectorAll('[data-gallery-item]'));
      document.addEventListener('product:variant-change', this.onVariantChange);
      this.updateAnimationIndexes();
    }

    disconnectedCallback() {
      document.removeEventListener('product:variant-change', this.onVariantChange);
    }

    onVariantChange(event) {
      const variant = event.detail?.variant || null;
      const eventSectionId = event.detail?.sectionId || null;

      if (this.sectionId && eventSectionId && this.sectionId !== String(eventSectionId)) return;
      if (!variant?.featured_media_id) return;

      this.updateVariantMedia(variant.featured_media_id);

      const targetItem = this.items.find((item) => item.dataset.mediaId === String(variant.featured_media_id));
      if (!targetItem) return;

      if (this.dataset.hideVariants !== 'true') {
        this.moveItemToFirst(targetItem);
      }

      this.items.forEach((item) => item.classList.toggle('is-active', item === targetItem));
      this.updateAnimationIndexes();

      if (this.dataset.hideVariants !== 'true') return;

      targetItem.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
      });
    }

    moveItemToFirst(item) {
      const list = item.closest('.product-gallery-desktop-list');
      if (!list || list.firstElementChild === item) return;

      list.prepend(item);
      this.items = Array.from(this.querySelectorAll('[data-gallery-item]'));
    }

    updateAnimationIndexes() {
      let animationIndex = 0;

      this.items.forEach((item) => {
        if (item.hidden) return;

        item.style.setProperty('--product-gallery-animation-index', String(animationIndex));
        animationIndex += 1;
      });
    }

    updateVariantMedia(featuredMediaId) {
      if (this.dataset.hideVariants !== 'true' || !featuredMediaId) return;

      const mediaId = String(featuredMediaId);

      this.items.forEach((item) => {
        if (!item.hasAttribute('data-variant-media')) return;
        item.hidden = item.dataset.mediaId !== mediaId;
      });

      this.updateAnimationIndexes();
    }
  }

  customElements.define('product-gallery-desktop', ProductGalleryDesktop);
}

document.addEventListener('change', (event) => {
  const select = event.target.closest('.product-main-form select[name="id"]');
  if (!select) return;

  const selectedOption = select.options[select.selectedIndex];
  const featuredMediaId = selectedOption?.dataset.featuredMediaId;
  if (!featuredMediaId) return;

  document.dispatchEvent(new CustomEvent('product:variant-change', {
    detail: {
      sectionId: select.closest('.product-main-form')?.dataset.sectionId,
      variant: {
        id: select.value,
        featured_media_id: featuredMediaId,
      },
    },
  }));
});
